import React from 'react';
import { Container, OtpInput, Stack, Text } from 'basis';
import { FormValidateOptions } from 'components';
import { FactorType } from 'types';
import styled from 'theme/styled';

const NUMERIC_REGEX = /^\d*$/;

function validate(value: string, { isEmpty, data }: FormValidateOptions) {
  if (isEmpty(value)) {
    return `Please enter your ${data.codeLength} code`;
  }
  const trimmedValue = value.trim();
  if (NUMERIC_REGEX.test(trimmedValue) === false) {
    return 'Only 0-9 are allowed';
  }
  const codeLength = Number(data.codeLength);
  if (trimmedValue.length !== codeLength) {
    return `Must have ${codeLength} digits`;
  }
  return null;
}

type Props = {
  factorType: FactorType;
  subject: string; // masked mobile number or email address
  width?: 'full' | 'auto';
  name: string;
  label: string;
  codeLength: number;
  loading: boolean;
  state: string;
  onResendSMSCodeClick?: (evt: React.SyntheticEvent<HTMLAnchorElement>) => void;
  onResendEmailCodeClick?: (evt: React.SyntheticEvent<HTMLAnchorElement>) => void;
};

type PropsResend = {
  forSmsLine?: boolean;
  loading: boolean;
  state: string;
  onClick?: (evt: React.SyntheticEvent<HTMLAnchorElement>) => void;
};

export const ResendBlock: React.FC<PropsResend> = ({ forSmsLine, loading, state, onClick }) => {
  const renderMessage = () => {
    if (loading || state === 'pending') {
      return (
        <Text>
          <strong>Sending...</strong>
        </Text>
      );
    }

    if (state === 'success') {
      return (
        <Text color="conditional.positive.text">
          <strong>Code sent</strong>
        </Text>
      );
    }

    if (state === 'error') {
      return (
        <Text color="conditional.negative.text">
          <strong>Error sending, please wait...</strong>
        </Text>
      );
    }

    if (forSmsLine) {
      return (
        <Text textStyle="body2">
          <ResendButton href="" newTab={false} onClick={onClick}>
            Resend code to your mobile
          </ResendButton>{' '}
          or
        </Text>
      );
    }

    return (
      <Text textStyle="body2">
        <ResendButton href="" newTab={false} onClick={onClick}>
          send code to your email address
        </ResendButton>
      </Text>
    );
  };

  return (
    <Stack gap="2" direction="horizontal">
      <Text>{forSmsLine ? 'Not received your code yet?' : 'alternatively'}</Text>
      {renderMessage()}
    </Stack>
  );
};
export const BasisOneTimeCode: React.FC<Props> = ({
  subject,
  name = 'oneTimeCode',
  codeLength = 6,
  width = 'full',
  loading,
  state,
  onResendSMSCodeClick,
  onResendEmailCodeClick,
  factorType,
}) => {
  return (
    <Stack gap="2">
      <Text textStyle="heading6">Enter your verification code</Text>
      <Text>
        We've just sent an {factorType} verification code to{' '}
        <strong>{factorType === 'sms' ? subject.replace(/[X*]/gi, '•') : subject}</strong>.
        <br />
        Please enter the {codeLength} digit code. The code will expire in 5 minutes.
      </Text>
      <Stack gap="2" width={width}>
        <div style={{ maxWidth: 300 }}>
          <OtpInput
            name={name}
            validate={validate}
            validateData={{ codeLength }}
            disabled={state === 'pending'}
            testId="verification-code"
            codeLength={Number(codeLength)}
            label=""
          />
        </div>
        <Container textStyle="body2">
          <ResendBlock onClick={onResendSMSCodeClick} loading={loading} state={state} forSmsLine />
          <ResendBlock onClick={onResendEmailCodeClick} loading={loading} state={state} />
        </Container>
      </Stack>
    </Stack>
  );
};

const ResendButton = styled.span`
  text-decoration: none;
  cursor: pointer;
  color: #0156f5;
  font-weight: 500;
`;
